/**
 * Screen 14 — Perfil.
 *
 * Account, portal language, password, session. The language picker is the profile's own
 * `locale` — `PATCH /api/v1/auth/me` — not a device setting: it decides the language of
 * this user's own invitation and password-reset letters as well as the portal's, which is
 * why `applyProfileLanguage` in `app/auth.tsx` applies it unconditionally on every login and
 * refresh. What it never changes is the **document**: the DeCA is a Spanish legal instrument
 * whose section names and field labels are fixed by `DICCIONARIO_CAMPOS`, and it is
 * generated in Spanish in either UI language (design spec §10).
 */

import { useState } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { unwrap } from '@deca/api'

import { api } from '../app/api.ts'
import { apiErrorMessage } from '../app/errors.ts'
import { useAuth } from '../app/auth.tsx'
import { roleKey } from '../deca/roles.ts'
import { SUPPORTED_LANGUAGES, type Language } from '../app/i18n.ts'
import { useLanguageChoice } from '../app/useLanguageChoice.ts'
import { applyTheme, readTheme, type ThemeChoice } from '../app/theme.ts'
import { AppShell } from '../shell/AppShell.tsx'
import { Button, Card, Datum, Notice } from '../ui/primitives.tsx'
import { SelectInput, TextInput } from '../ui/form.tsx'
import { useToast } from '../ui/Toast.tsx'

export function ProfileScreen() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const toast = useToast()
  const { principal, signOut } = useAuth()

  // The picker persists to the server — `PATCH /api/v1/auth/me` — through the same hook the
  // TopBar toggle uses (`app/useLanguageChoice.ts`), which is also what keeps the two in
  // sync while both are on screen: this screen renders inside `AppShell`, so the TopBar is
  // always mounted alongside it.
  const { language, choose: switchLanguage } = useLanguageChoice()
  const [theme, setTheme] = useState<ThemeChoice>(() => readTheme())

  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [repeatPassword, setRepeatPassword] = useState('')
  const [passwordError, setPasswordError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  async function changePassword() {
    setPasswordError(null)
    if (newPassword !== repeatPassword) {
      setPasswordError(t('auth.passwordsDiffer'))
      return
    }
    if (newPassword.length < 12) {
      setPasswordError(t('auth.passwordMinLength', { count: 12 }))
      return
    }
    setBusy(true)
    try {
      await unwrap(api.POST('/api/v1/auth/password', { body: { currentPassword, newPassword } }))
      setCurrentPassword('')
      setNewPassword('')
      setRepeatPassword('')
      toast.success(t('auth.passwordChanged'))
    } catch (caught) {
      setPasswordError(apiErrorMessage(caught, t))
    } finally {
      setBusy(false)
    }
  }

  return (
    <AppShell title={t('profile.title')} subtitle={t('profile.subtitle')}>
      <div className="two-col">
        <Card title={t('profile.account')}>
          <div className="stack">
            <Datum label={t('profile.email')} value={principal?.email} />
            <Datum label={t('profile.organisation')} value={principal?.organisation.legalName} />
            <Datum
              label={t('profile.role')}
              value={principal?.role ? t(roleKey(principal.role)) : null}
            />
            {principal?.role ? (
              <p className="small muted">{t(`role.description.${principal.role}`)}</p>
            ) : null}
          </div>
        </Card>

        <Card title={t('profile.preferences')}>
          <div className="stack">
            <SelectInput
              label={t('common.language')}
              help={t('profile.languageHelp')}
              value={language}
              onChange={(event) => void switchLanguage(event.target.value as Language)}
            >
              {SUPPORTED_LANGUAGES.map((value) => (
                <option key={value} value={value}>
                  {value === 'es' ? 'Español (es-ES)' : 'English (en)'}
                </option>
              ))}
            </SelectInput>

            <SelectInput
              label={t('common.theme')}
              value={theme}
              onChange={(event) => {
                const next = event.target.value as ThemeChoice
                setTheme(next)
                applyTheme(next)
              }}
            >
              <option value="system">{t('common.themeSystem')}</option>
              <option value="light">{t('common.themeLight')}</option>
              <option value="dark">{t('common.themeDark')}</option>
            </SelectInput>
          </div>
        </Card>

        <Card title={t('profile.security')}>
          <div className="stack">
            {passwordError ? <Notice tone="danger">{passwordError}</Notice> : null}
            <TextInput
              label={t('auth.currentPassword')}
              type="password"
              autoComplete="current-password"
              value={currentPassword}
              onChange={(event) => setCurrentPassword(event.target.value)}
            />
            <TextInput
              label={t('auth.newPassword')}
              type="password"
              autoComplete="new-password"
              help={t('auth.passwordMinLength', { count: 12 })}
              value={newPassword}
              onChange={(event) => setNewPassword(event.target.value)}
            />
            <TextInput
              label={t('auth.repeatPassword')}
              type="password"
              autoComplete="new-password"
              value={repeatPassword}
              onChange={(event) => setRepeatPassword(event.target.value)}
            />
            <div>
              <Button
                variant="primary"
                loading={busy}
                disabled={busy || !currentPassword || !newPassword}
                onClick={() => void changePassword()}
              >
                {t('auth.changePassword')}
              </Button>
            </div>
          </div>
        </Card>

        <Card title={t('profile.session')}>
          <div className="stack">
            <p className="small muted">{t('profile.logoutHelp')}</p>
            <div>
              <Button
                variant="danger"
                icon="logout"
                onClick={() => void signOut().then(() => navigate({ to: '/login' }))}
              >
                {t('common.logout')}
              </Button>
            </div>
          </div>
        </Card>
      </div>
    </AppShell>
  )
}
